import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";

import { Flex, Text } from "../../../styles/utility-components";
import SkeletonText from "../skeleton/SkeletonText";
import Tooltip from "../tooltip/Tooltip";
import { LabelOverride, useRouteBreadcrumbs } from "./useRouteBreadcrumbs";

export interface BreadcrumbItem {
  label: string;
  path?: string;
  isLoading?: boolean;
}

interface BaseBreadcrumbProps {
  items: BreadcrumbItem[];
  onNavigate: (path: string) => void;
}

function BaseBreadcrumb({ items, onNavigate }: BaseBreadcrumbProps) {
  const renderBreadcrumbContent = (label: string, isLink: boolean) => (
    <Tooltip content={label} side="bottom">
      <TruncatedText isLink={isLink}>{label}</TruncatedText>
    </Tooltip>
  );

  const renderBreadcrumbItem = (item: BreadcrumbItem) => {
    if (item.isLoading) {
      return <SkeletonText width="80px" fontSize={14} lineHeight={1.57} />;
    }

    if (item.path !== undefined) {
      return (
        <BreadcrumbLink
          onClick={() => {
            if (item.path !== undefined) {
              onNavigate(item.path);
            }
          }}
        >
          {renderBreadcrumbContent(item.label, true)}
        </BreadcrumbLink>
      );
    }

    return (
      <Text as="span" color="sysTextDefault">
        {renderBreadcrumbContent(item.label, false)}
      </Text>
    );
  };

  return (
    <Flex alignItems="center" gap={8}>
      {items.map((item, index) => (
        <Flex key={index} alignItems="center" gap={8}>
          {renderBreadcrumbItem(item)}
          {index < items.length - 1 && <Text color="sysTextSecondary"> / </Text>}
        </Flex>
      ))}
    </Flex>
  );
}

interface SimpleBreadcrumbProps {
  items: BreadcrumbItem[];
  onNavigate?: (path: string) => void;
}

/**
 * Breadcrumb with an explicit `items` list. Defaults to TanStack Router for
 * navigation; pass `onNavigate` to override. For auto-generated breadcrumbs from
 * the route hierarchy, use RouterBreadcrumb instead.
 */
export function SimpleBreadcrumb({ items, onNavigate }: SimpleBreadcrumbProps) {
  const navigate = useNavigate();
  return (
    <BaseBreadcrumb
      items={items}
      onNavigate={onNavigate ?? ((path) => void navigate({ to: path }))}
    />
  );
}

interface RouterBreadcrumbProps {
  /** Keys are resolved pathnames (e.g. `/bids/${id}`) to provide dynamic labels for route segments. */
  overrides?: Record<string, LabelOverride>;
}

/**
 * Breadcrumb that auto-generates items from the TanStack Router route hierarchy.
 * Static labels come from `staticData.breadcrumbLabel` (or `pageName` fallback).
 * Pass `overrides` for dynamic segments like entity names.
 */
export function RouterBreadcrumb({ overrides }: RouterBreadcrumbProps) {
  const navigate = useNavigate();
  const items = useRouteBreadcrumbs(overrides);
  return <BaseBreadcrumb items={items} onNavigate={(path) => void navigate({ to: path })} />;
}

export const BreadcrumbContainer = styled.div(({ theme }) => ({
  display: "flex",
  minHeight: 65,
  height: 65,
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  boxSizing: "border-box",
  flexShrink: 0,
}));

const BreadcrumbLink = styled.button(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  color: theme.colors.sysTextSecondary,
  borderRadius: 2,

  "&:hover": {
    backgroundColor: `${theme.colors.sysTextDefault}0A`, // 4% opacity
  },
  "&:active": {
    backgroundColor: `${theme.colors.sysTextDefault}14`, // 8% opacity
  },
}));

const TruncatedText = styled.span<{ isLink: boolean }>(({ isLink }) => ({
  display: "inline-block",
  maxWidth: 200,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
  wordBreak: "break-all",
  // Apply underline hover/active only if it's a link
  ...(isLink && {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
    "&:active": {
      textDecoration: "underline",
    },
  }),
}));
